// JobRowActionsMenu — per-row "Actions ▾" dropdown for the
// SkipJobsListPage. Mirrors the SkipJobDetailPage's
// DynamicActionButtons but compressed into a popover.
//
// Behaviour:
//   - Only mounts the available-actions query once the menu is open
//     (lazy fetch — pages with 25 rows would otherwise fire 25
//     /available-actions requests at mount).
//   - One dropdown open at a time at the page level — the parent
//     tracks `openEncId` and passes `isOpen` down.
//   - Selecting an action opens the matching modal (collection /
//     exchange / tip_return / confirm). Modal onSuccess invalidates
//     the jobs list query so the row reflects the new state.
//   - Terminal-stage rows skip the dropdown entirely; the parent
//     decides which to render based on job.status.
//
// CSS variables only — no inline colours.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAvailableActions } from '../../hooks/useSkipJobs';
import { queryKeys } from '../../../../api/queryKeys';
import { ConfirmActionModal } from './ConfirmActionModal';
import { MarkDeliveredModal } from './MarkDeliveredModal';
import { ProcessCollectionModal } from './ProcessCollectionModal';
import { ProcessExchangeModal } from './ProcessExchangeModal';
import { ProcessTipReturnModal } from './ProcessTipReturnModal';
import { getActionKeyConfig } from './actionKeyEndpoints';

import styles from './JobRowActionsMenu.module.css';

export interface JobRowActionsMenuProps {
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  /** Bug 3 — propagated to ProcessExchangeModal so the replacement
   *  inventory dropdown filters by the parent job's chosen size. */
  skipSizeEncId: string | null;
  /** True iff this row's dropdown is the one currently open. */
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export function JobRowActionsMenu({
  jobEncId,
  jobNumber,
  customerName,
  skipSizeEncId,
  isOpen,
  onToggle,
  onClose,
}: JobRowActionsMenuProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();
  // Portal'd menu position. The dropdown is rendered through a portal
  // at <body> so it isn't clipped by any ancestor with overflow:hidden
  // (typical for table rows / cards). Coordinates are recomputed
  // whenever the menu opens, on resize, and on scroll.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(
    null,
  );
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    function recompute() {
      const node = toggleRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [isOpen]);

  // Lazy-mount the actions fetch — only fire when the menu has been
  // opened at least once. The query stays cached after close so a
  // re-open inside the staleTime window doesn't re-fetch.
  const { data, isLoading } = useAvailableActions(isOpen ? jobEncId : null);

  // Close on outside click. Mounted only while open.
  // Mousedowns inside any portal-rendered modal (role="dialog") are
  // ignored — ConfirmActionModal etc. live in a portal at document.body
  // (NOT inside wrapperRef), so without this guard typing in the modal's
  // textarea would close the dropdown and unmount the modal mid-keystroke.
  useEffect(() => {
    if (!isOpen) return;
    function handleMouseDown(e: MouseEvent) {
      const node = wrapperRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      const target = e.target as Element | null;
      // The menu itself is rendered through a portal at <body>, so a
      // mousedown on a menu item lands outside wrapperRef. Exempt
      // role="menu" alongside role="dialog" (modals) so neither
      // closes the dropdown mid-action.
      if (target?.closest('[role="dialog"]')) return;
      if (target?.closest('[role="menu"]')) return;
      onClose();
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, onClose]);

  // The action_key currently mounting a modal; null = no modal.
  // Stored separately from `isOpen` because the modal lives outside
  // the wrapperRef and clicking it must NOT close-on-outside.
  const [openActionKey, setOpenActionKey] = useState<string | null>(null);

  function handleAfterAction(): void {
    setOpenActionKey(null);
    onClose();
    void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
    void qc.invalidateQueries({
      queryKey: queryKeys.skipJobs.availableActions(jobEncId),
    });
  }

  const actions = (data?.actions ?? [])
    .slice()
    .sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return a.sort_order - b.sort_order;
    });

  const openAction =
    openActionKey === null ? null : actions.find((a) => a.action_key === openActionKey) ?? null;
  const openConfig = openActionKey === null ? null : getActionKeyConfig(openActionKey);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        ref={toggleRef}
        type="button"
        className={styles.toggle}
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        Actions
        <span aria-hidden>▾</span>
      </button>

      {isOpen && menuPos !== null
        ? createPortal(
            <div
              className={styles.menu}
              role="menu"
              style={{
                position: 'fixed',
                top: `${menuPos.top}px`,
                right: `${menuPos.right}px`,
                zIndex: 1100,
              }}
            >
              {isLoading ? (
                <div className={styles.loading}>Loading actions…</div>
              ) : actions.length === 0 ? (
                <div className={styles.empty}>No actions available</div>
              ) : (
                actions.map((action) => {
                  const config = getActionKeyConfig(action.action_key);
                  const disabled = config === null;
                  return (
                    <button
                      key={action.action_key}
                      type="button"
                      role="menuitem"
                      className={[
                        styles.item,
                        action.is_primary ? styles.itemPrimary : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setOpenActionKey(action.action_key);
                      }}
                    >
                      {action.button_label}
                    </button>
                  );
                })
              )}
            </div>,
            document.body,
          )
        : null}

      {/* Modals — rendered outside the dropdown so an outside-click
          on the modal does not close the dropdown's wrapper. */}
      {openAction !== null && openConfig?.modalType === 'confirm' ? (
        <ConfirmActionModal
          isOpen
          jobEncId={jobEncId}
          action={openAction}
          onClose={handleAfterAction}
        />
      ) : null}
      {openConfig?.modalType === 'collection' ? (
        <ProcessCollectionModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          onClose={handleAfterAction}
        />
      ) : null}
      {openConfig?.modalType === 'exchange' ? (
        <ProcessExchangeModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          skipSizeEncId={skipSizeEncId}
          onClose={handleAfterAction}
        />
      ) : null}
      {openConfig?.modalType === 'tip_return' ? (
        <ProcessTipReturnModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          onClose={handleAfterAction}
        />
      ) : null}
      {openConfig?.modalType === 'mark_delivered' ? (
        <MarkDeliveredModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          onClose={handleAfterAction}
        />
      ) : null}
    </div>
  );
}

